import { useState } from "react"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  RefreshCw,
  Share2,
  RotateCcw,
  Save,
  Download,
  SlidersHorizontal,
  LayoutList,
  GanttChart,
  Kanban,
  LayoutDashboard,
  ChevronDown,
  Trash2,
} from "lucide-react"
import type { SharedReportListItem, ViewMode } from "@/types/report"
import { cn } from "@/lib/utils"

interface ReportToolbarProps {
  onRefresh: () => void
  onShare: () => void
  onReset: () => void
  onSaveView: () => void
  onExport: () => void
  onToggleFilters: () => void
  filtersVisible: boolean
  views: SharedReportListItem[]
  activeViewId: number | null
  onSelectView: (stateId: number | null) => void
  onDeleteView: (stateId: number) => void
  canShare: boolean
  viewMode: ViewMode
  onViewModeChange: (mode: ViewMode) => void
  availableViews: ViewMode[]
  activeFilterCount?: number
}

const viewIcons: Record<ViewMode, { icon: typeof LayoutList; label: string }> = {
  table: { icon: LayoutList, label: "Table" },
  gantt: { icon: GanttChart, label: "Gantt" },
  kanban: { icon: Kanban, label: "Kanban" },
  dashboard: { icon: LayoutDashboard, label: "Dashboard" },
}

export function ReportToolbar({
  onRefresh,
  onShare,
  onReset,
  onSaveView,
  onExport,
  onToggleFilters,
  filtersVisible,
  views,
  activeViewId,
  onSelectView,
  onDeleteView,
  canShare,
  viewMode,
  onViewModeChange,
  availableViews,
  activeFilterCount = 0,
}: ReportToolbarProps) {
  const [viewDropdownOpen, setViewDropdownOpen] = useState(false)
  const activeView = views.find((v) => v.userProfileSearchStateId === activeViewId)

  return (
    <div className="bg-white border-b border-slate-200 shadow-sm">
      <div className="flex items-center justify-between px-6 py-3">
        {/* Left: View selector + view mode toggle */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500">Report:</span>
            <Popover open={viewDropdownOpen} onOpenChange={setViewDropdownOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-[160px] h-9 text-sm justify-between font-normal">
                  <span className="truncate">{activeView?.stateName ?? "Default View"}</span>
                  <ChevronDown className="w-3.5 h-3.5 ml-1 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-[220px] p-1">
                <button
                  className={cn(
                    "flex items-center w-full px-2 py-1.5 text-sm rounded-sm hover:bg-slate-100 transition-colors",
                    !activeViewId && "bg-slate-100 font-medium"
                  )}
                  onClick={() => { onSelectView(null); setViewDropdownOpen(false) }}
                >
                  Default View
                </button>
                {views.map((view) => (
                  <div
                    key={view.userProfileSearchStateId}
                    className={cn(
                      "flex items-center group rounded-sm hover:bg-slate-100 transition-colors",
                      activeViewId === view.userProfileSearchStateId && "bg-slate-100"
                    )}
                  >
                    <button
                      className={cn(
                        "flex-1 text-left px-2 py-1.5 text-sm truncate",
                        activeViewId === view.userProfileSearchStateId && "font-medium"
                      )}
                      onClick={() => { onSelectView(view.userProfileSearchStateId); setViewDropdownOpen(false) }}
                    >
                      {view.stateName}
                    </button>
                    <button
                      className="p-1 mr-1 text-slate-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={(e) => { e.stopPropagation(); onDeleteView(view.userProfileSearchStateId) }}
                      title="Delete view"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </PopoverContent>
            </Popover>
          </div>

          {/* View mode toggle */}
          {availableViews.length > 1 && (
            <div className="flex items-center border border-slate-200 rounded-md overflow-hidden">
              {availableViews.map((mode) => {
                const { icon: Icon, label } = viewIcons[mode]
                const isActive = viewMode === mode
                return (
                  <button
                    key={mode}
                    onClick={() => onViewModeChange(mode)}
                    className={cn(
                      "flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors border-r last:border-r-0 border-slate-200",
                      isActive
                        ? "bg-slate-700 text-white"
                        : "bg-white text-slate-600 hover:bg-slate-50"
                    )}
                    title={label}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {label}
                  </button>
                )
              })}
            </div>
          )}
        </div>

        {/* Right: action buttons */}
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="h-9 gap-2" onClick={onRefresh}>
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
          <Button variant="outline" size="sm" className="h-9 gap-2" onClick={onShare} disabled={!canShare}>
            <Share2 className="w-4 h-4" />
            Share
          </Button>
          <Button variant="outline" size="sm" className="h-9 gap-2" onClick={onReset}>
            <RotateCcw className="w-4 h-4" />
            Reset
          </Button>
          <Button variant="outline" size="sm" className="h-9 gap-2" onClick={onSaveView}>
            <Save className="w-4 h-4" />
            Save View
          </Button>
          <Button
            variant="outline"
            size="sm"
            aria-pressed={filtersVisible}
            className={`h-9 gap-2 ${filtersVisible ? "bg-slate-100 border-slate-400" : ""}`}
            onClick={onToggleFilters}
          >
            <SlidersHorizontal className="w-4 h-4" />
            Filters
            {activeFilterCount > 0 && (
              <span className="bg-slate-700 text-white text-[10px] rounded-full px-1.5 py-0.5 leading-none">
                {activeFilterCount}
              </span>
            )}
          </Button>
          <Button variant="outline" size="sm" className="h-9 gap-2" onClick={onExport}>
            <Download className="w-4 h-4" />
            Export
          </Button>
        </div>
      </div>
    </div>
  )
}
